<!--
  Navbar pública — Figma "Site Desktop" > Navbar (1229:3311, 1440x82).
  Branco a 50% com backdrop-blur 13.6 e filete inferior; fica fixa sobre o hero.
-->
<script setup lang="ts">
import { ref } from "vue";
import { RouterLink } from "vue-router";
import FlagsLanguage from "../FlagsLanguage.vue";
import { LangType } from "@/helpers/types";

// os rótulos reaproveitam headerComponent.*, que já existia nos três idiomas
const links = [
  { to: "/", label: "headerComponent.home" },
  { to: "/sobre-nos", label: "headerComponent.aboutUs" },
  { to: "/planos", label: "headerComponent.plans" },
  { to: "/contato", label: "headerComponent.contact" },
];

const langs = [LangType.pt_BR, LangType.en_US, LangType.es_ES];

const menuOpen = ref(false);
</script>

<template>
  <header class="hv2 nav2">
    <div class="nav2__inner">
      <RouterLink to="/" class="nav2__logo" aria-label="Selfie Check-in — início">
        <img src="/img/home/v2/logo-selfie.png" alt="Selfie Check-in" width="124" height="60" />
      </RouterLink>

      <nav class="nav2__links" :class="{ 'is-open': menuOpen }">
        <!-- "/" casa com qualquer rota como active-class, então usa exact -->
        <RouterLink
          v-for="link in links"
          :key="link.to"
          :to="link.to"
          class="nav2__link"
          :active-class="link.to === '/' ? '' : 'is-active'"
          exact-active-class="is-active"
          @click="menuOpen = false"
        >
          {{ $t(link.label) }}
        </RouterLink>

        <div class="nav2__actions nav2__actions--mobile">
          <FlagsLanguage :supported-languages="langs" />
          <RouterLink class="hv2-btn hv2-btn--ghost" to="/login" @click="menuOpen = false">
            {{ $t("headerComponent.login") }}
          </RouterLink>
          <RouterLink class="hv2-btn hv2-btn--primary" to="/register" @click="menuOpen = false">
            {{ $t("headerComponent.register") }}
          </RouterLink>
        </div>
      </nav>

      <div class="nav2__actions nav2__actions--desktop">
        <FlagsLanguage :supported-languages="langs" />
        <RouterLink class="hv2-btn hv2-btn--ghost nav2__login" to="/login">
          {{ $t("headerComponent.login") }}
        </RouterLink>
        <RouterLink class="hv2-btn hv2-btn--primary nav2__signup" to="/register">
          {{ $t("headerComponent.register") }}
        </RouterLink>
      </div>

      <button
        class="nav2__burger"
        type="button"
        :aria-expanded="menuOpen"
        aria-label="Abrir menu"
        @click="menuOpen = !menuOpen"
      >
        <span></span><span></span><span></span>
      </button>
    </div>
  </header>
</template>

<style scoped>
.nav2 {
  position: sticky;
  top: 0;
  z-index: 50;
  width: 100%;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(13.6px);
  -webkit-backdrop-filter: blur(13.6px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.nav2__inner {
  width: 100%;
  max-width: var(--hv2-max);
  min-height: 82px;
  margin-inline: auto;
  padding-inline: var(--hv2-gutter);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.nav2__logo img {
  display: block;
  width: 124px;
  height: auto;
}

/* --- links (Inter) -------------------------------------------------- */

.nav2__links {
  display: flex;
  align-items: center;
  gap: 32px;
}

.nav2__link {
  font-family: var(--hv2-font-ui);
  font-weight: 400;
  font-size: 16px;
  line-height: 23.2px;
  letter-spacing: -0.08px;
  color: rgba(0, 0, 0, 0.55);
  text-decoration: none;
  transition: color 0.18s ease;
}

.nav2__link:hover {
  color: var(--hv2-ink);
}

.nav2__link.is-active {
  color: var(--hv2-accent);
  font-weight: 500;
}

/* --- ações ---------------------------------------------------------- */

.nav2__actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.nav2__login {
  height: 49px;
}

.nav2__signup {
  height: 45px;
}

.nav2__actions--mobile {
  display: none;
}

/* --- menu mobile ---------------------------------------------------- */

.nav2__burger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 44px;
  height: 44px;
  padding: 0 10px;
  background: none;
  border: 0;
  cursor: pointer;
}

.nav2__burger span {
  display: block;
  height: 2px;
  background: var(--hv2-ink);
  border-radius: 2px;
}

@media (max-width: 1000px) {
  /*
   * O menu aberto ocupa a linha inteira (flex-basis 100%). Como o botão
   * hambúrguer vem depois dele no DOM, sem `order` ele era empurrado para
   * baixo do menu. O `order` mantém logo | botão na primeira linha e o
   * menu na segunda.
   */
  .nav2__inner {
    min-height: 72px;
    flex-wrap: wrap;
    align-items: center;
  }

  .nav2__logo {
    order: 1;
  }

  .nav2__burger {
    order: 2;
  }

  .nav2__links {
    order: 3;
  }

  .nav2__actions--desktop {
    display: none;
  }

  .nav2__burger {
    display: flex;
  }

  .nav2__links {
    display: none;
    flex-basis: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    padding-bottom: 20px;
  }

  .nav2__links.is-open {
    display: flex;
  }

  .nav2__link {
    padding: 12px 0;
    font-size: 17px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .nav2__actions--mobile {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding-top: 16px;
  }
}
</style>
